/**
 * Device-local HEARD memory with time-decay.
 *
 * Why this exists: when the user stops a tour and starts again 10
 * minutes later at the same spot, the bot used to bring the same
 * topics — same Lindwurmbrunnen, same Minimundus. The server-side
 * /interactions/heard endpoint was disabled because it's
 * cross-device-global and over-restricts famous landmarks. This
 * module replaces it with a per-device, time-decaying, radius-gated
 * memory.
 *
 * Three tiers:
 *   - 0-2h     → HARD-block. Topic gets injected as [HEARD:title,...]
 *                in the GPS message. Bot prompt says: never as main.
 *   - 2-24h    → SOFT-context. Topic appears in [HEARD-RECENT:...].
 *                Bot may bring it but MUST find a new angle.
 *   - >24h     → silently pruned from localStorage on the next read.
 *
 * Radius gate: only topics heard within 500m of the current GPS
 * position are flagged. A user who heard about Lindwurmbrunnen
 * yesterday in the Innenstadt and is today on the Heuplatzgasse
 * (within radius) gets the block. Same user in Wien tomorrow does
 * not — Wien is far, the Innenstadt entry is irrelevant context.
 *
 * Design contract documented in content-api post 808.
 */

const STORAGE_KEY = 'wanderlaut-heard';
const RADIUS_M = 500;
const HARD_MIN = 120;   // 2h
const SOFT_MIN = 1440;  // 24h
const MAX_ENTRIES = 500;
const MAX_TAG_TITLES = 10;  // per tier, keep tag string sane

export interface HeardEntry {
  /** Topic.title at the time it was spoken. */
  title: string;
  /** User position when the topic played (NOT the topic's lat/lon). */
  lat: number;
  lon: number;
  /** Unix seconds. */
  ts: number;
}

function readHeard(): HeardEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(e =>
      e && typeof e.title === 'string'
      && typeof e.lat === 'number'
      && typeof e.lon === 'number'
      && typeof e.ts === 'number',
    );
  } catch {
    return [];
  }
}

function writeHeard(entries: HeardEntry[]): void {
  // Cap by recency — keep newest, drop oldest if we'd blow past MAX.
  const trimmed = entries.length <= MAX_ENTRIES
    ? entries
    : entries.slice(-MAX_ENTRIES);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // Storage full / disabled — silently ignore. The cost is one
    // extra "Lindwurmbrunnen", not a broken app.
  }
}

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Record that the bot just spoke about `title` while the user was at
 * (lat, lon). Idempotent on (title, position) tuples that already
 * exist within the same minute — guards against double-emit of
 * package:start.
 */
export function pushHeard(title: string, lat: number, lon: number): void {
  const t = (title || '').trim();
  if (!t || lat == null || lon == null) return;
  const now = Math.floor(Date.now() / 1000);
  const arr = readHeard();

  // Dedupe: same title within 60s already counts.
  const recent = arr.find(e =>
    e.title === t && (now - e.ts) < 60 && haversineM(e.lat, e.lon, lat, lon) < 50,
  );
  if (recent) return;

  arr.push({ title: t, lat, lon, ts: now });
  writeHeard(arr);
}

/**
 * Build the HEARD / HEARD-RECENT tag string for the GPS message
 * about to be sent. Side-effect: prunes entries older than SOFT_MIN
 * from localStorage so the store doesn't grow unbounded.
 *
 * Returns a string suffix like
 *   "[HEARD:Lindwurmbrunnen,Domkirche] [HEARD-RECENT:Stadtgasse]"
 * or "" if nothing applies.
 */
export function getHeardTag(lat: number, lon: number): string {
  const now = Math.floor(Date.now() / 1000);
  const arr = readHeard();
  const hard: string[] = [];
  const soft: string[] = [];
  const keep: HeardEntry[] = [];

  for (const e of arr) {
    const ageMin = (now - e.ts) / 60;
    if (ageMin >= SOFT_MIN) continue; // forgotten
    keep.push(e);
    const distM = haversineM(e.lat, e.lon, lat, lon);
    if (distM > RADIUS_M) continue;   // out of range — not flagged
    if (ageMin < HARD_MIN) hard.push(e.title);
    else                    soft.push(e.title);
  }

  if (keep.length !== arr.length) writeHeard(keep);

  // Dedupe titles within each tier, take newest-first up to MAX_TAG_TITLES.
  // Using Set preserves insertion order, and we already iterate in
  // localStorage order which is push-order (chronological).
  const dedupTop = (xs: string[]) => Array.from(new Set(xs.reverse())).slice(0, MAX_TAG_TITLES);
  const hardStr = dedupTop(hard).join(',');
  const softStr = dedupTop(soft).join(',');

  const parts: string[] = [];
  if (hardStr) parts.push(`[HEARD:${hardStr}]`);
  if (softStr) parts.push(`[HEARD-RECENT:${softStr}]`);
  return parts.join(' ');
}
